import { KenoBoard, useKenoReveal } from './KenoBoard.js';
import { ticketColor, ticketResultLabel, type KenoBoardTicket } from '@tv-casino/viewmodels/components/kenoBoardModel.js';
import styles from './KenoTable.module.css';

/** What the TV felt needs to draw one keno window. */
export interface KenoTableView {
  /** The draw the cells light up, or null before the draw. */
  draw: number[] | null;
  /** True once the draw has settled: the reveal is over and results show. */
  settled: boolean;
  /** The video-keno cabinet and lounge-card hues when the host asked for them; otherwise the table's felt and seat colours. */
  classicColors: boolean;
  /** Every ticket at the table, seat order, each in its table colour. */
  tickets: KenoBoardTicket[];
  seatSlates: {
    seatIndex: number;
    name: string | null;
    done: boolean;
    tickets: {
      id: number;
      label: string;
      amount: number;
      payout: number | null;
      catches: number[];
      /** The same colour this ticket wears on the board and the owner's phone. */
      colorIndex: number;
    }[];
  }[];
  /** The most-drawn numbers across the history window, best first. */
  hot: { spot: number; count: number }[];
}

/**
 * The keno half of the TV: the 80-board is the centerpiece, flanked by the
 * heat board on its left and the seat slates on its right, so the felt reads
 * edge to edge the way the roulette and craps felts do. The balls light
 * across the board between the draw landing and the settle deadline, so the
 * reveal finishes exactly when the chips move. Reduced motion lands the whole
 * draw at once. Every ticket at the table stacks on the board in its own
 * colour, and the slate swatches and the phones use the same colours.
 */
export function KenoTable({ view, endsAt }: { view: KenoTableView; endsAt: number | null }) {
  const reveal = useKenoReveal(view.draw, view.settled, endsAt);
  return (
    <div className={view.classicColors ? `${styles.felt} ${styles.classic}` : styles.felt}>
      <div className={styles.heatSide}>
        {view.hot.length > 0 && (
          <div className={styles.heat}>
            <span className={styles.hotLabel}>Hottest numbers</span>
            <div className={styles.hot} role="list" aria-label="Hottest numbers">
              {view.hot.map((entry) => (
                <span
                  key={entry.spot}
                  role="listitem"
                  className={styles.hotCell}
                  title={`${entry.count} hits`}
                >
                  {entry.spot}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className={styles.boardSide}>
        <KenoBoard
          size="tv"
          draw={view.draw}
          tickets={view.tickets}
          reveal={reveal}
          classicColors={view.classicColors}
        />
      </div>
      <div className={styles.rail}>
        {view.seatSlates.map((slate) => (
          <div key={slate.seatIndex} className={styles.slate}>
            <span className={styles.slateName}>
              {slate.name ?? `Seat ${slate.seatIndex + 1}`}
              {slate.done ? ' · in' : ''}
            </span>
            {slate.tickets.map((ticket) => (
              <span key={ticket.id} className={styles.slateTicket}>
                <span
                  className={styles.swatch}
                  style={{ background: ticketColor(ticket.colorIndex, view.classicColors) }}
                  aria-hidden="true"
                />
                <span>
                  {ticket.label} · {ticket.amount.toLocaleString()}
                  {ticket.payout != null && (
                    <span className={styles.slatePayout}>
                      {` · caught ${ticket.catches.length} · ${ticketResultLabel(
                        ticket.amount,
                        ticket.payout,
                      )}`}
                    </span>
                  )}
                </span>
              </span>
            ))}
          </div>
        ))}
        {view.seatSlates.length === 0 && view.hot.length === 0 && (
          <p className={styles.railPrompt}>Tickets appear here</p>
        )}
      </div>
    </div>
  );
}
